import { motion, AnimatePresence } from 'framer-motion'
import type { ShoutoutEvent } from '@gymboard/types'

interface Props {
  event: ShoutoutEvent | null
}

export default function ShoutoutToast({ event }: Props) {
  return (
    <AnimatePresence>
      {event && (
        <motion.div
          key={`${event.athleteId}-${event.message}`}
          initial={{ opacity: 0, x: 100, scale: 0.85, rotateY: 15 }}
          animate={{ opacity: 1, x: 0, scale: 1, rotateY: 0 }}
          exit={{ opacity: 0, x: 100, scale: 0.9 }}
          transition={{ duration: 0.45, ease: [0.175, 0.885, 0.32, 1.275] }}
          className="absolute top-6 right-6 z-40 pointer-events-none"
          style={{ maxWidth: 340 }}
        >
          <div
            className="rounded-2xl p-5 backdrop-blur-md overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(5,30,15,0.97) 0%, rgba(5,40,20,0.97) 100%)',
              border: '1.5px solid rgba(34,197,94,0.45)',
              boxShadow: '0 0 0 1px rgba(34,197,94,0.12), 0 0 40px rgba(34,197,94,0.25), 0 12px 40px rgba(0,0,0,0.6)',
            }}
          >
            {/* Top bar */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="absolute top-0 left-0 right-0 h-0.5 origin-left"
              style={{ background: 'linear-gradient(90deg, #30D158, #86efac, #30D158)' }}
            />

            <div className="flex items-start gap-3">
              {/* Megaphone */}
              <motion.div
                animate={{
                  rotate: [0, -12, 12, -8, 8, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-3xl flex-shrink-0 mt-0.5"
              >
                📣
              </motion.div>

              <div className="min-w-0">
                {/* Label */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-green-400 text-xs font-black tracking-[0.2em] uppercase mb-2"
                >
                  Coach Shoutout
                </motion.p>

                {/* Athlete name */}
                <motion.p
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.18 }}
                  className="text-white font-black text-lg leading-tight truncate"
                >
                  {event.athleteName}
                </motion.p>

                {/* Message */}
                {event.message && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.28 }}
                    className="text-white/65 text-sm mt-1.5 leading-relaxed"
                  >
                    "{event.message}"
                  </motion.p>
                )}

                {/* Coach */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.38 }}
                  className="text-white/30 text-xs mt-2"
                >
                  from {event.coachName}
                </motion.p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
